import React from 'react';
import { Typography, Theme } from '@mui/material';
import { WithTranslation, withTranslation, Trans } from 'react-i18next';
import { createStyles, WithStyles, withStyles } from '@mui/styles';
import { CashAccepterIcon } from 'assets/icons';
import { Billpay } from 'models/kiosk/Billpay';
import AmountDisplay from './AmountToDisplay';
import PaymentError from './PaymentError';


interface PaymentBodyProps
    extends WithStyles<typeof styles>,
    WithTranslation {
    billpay: Billpay;
    errorMessage: string | null;
}


const styles = (theme: Theme) =>
    createStyles({
        body: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            marginTop: '2vh',
        },
        title: {
            color: theme.palette.primary.main,
            fontSize: '3.5rem',
            maxWidth: '600px',
            textAlign: 'center',
            lineHeight: '100px',
        },
        cashAccepterIcon: {
            marginTop: '96px',
        },
        cashWarning: {
            marginTop: '68px',
            display: 'flex',
            textAlign: 'center',
            alignItems: 'center',
        },
        cashWarningText: {
            fontSize: '1.75rem',
        },
        amounts: {
            marginTop: '80px',
            '& >div': {
                display: 'flex',
                alignItems: 'flex-end',
                overflow: 'hidden',
                maxHeight: '37px',
                marginTop: '40px',
            },
        },
        amountInserted: {
            border: `2px solid ${theme.palette.primary.main} `,
            borderRadius: '10px',
            padding: '28px 72px',
            color: theme.palette.primary.main,
            maxHeight: '95px !important',
        },
        insertedAmountTitle: {
            letterSpacing: '2px',
        },
        insertedAmountValue: {
            fontWeight: 'bold',
            marginLeft: 'auto',
        },
        underline: {
            marginTop: '40px',
            borderTop: '1px solid ',
        },
    });

const PaymentBody: React.FC<PaymentBodyProps> = (({ errorMessage, billpay, t, classes }) => {
    return (
        <div className={classes.body}>
            <Typography variant='h2' className={classes.title}>
                {t('Insert Cash')}
            </Typography>
            <div className={classes.cashAccepterIcon}>
                <CashAccepterIcon colorType='primary' />
            </div>
            <PaymentError
                errorMessage={errorMessage}
            />
            <div className={classes.cashWarning}>
                <Typography
                    className={classes.cashWarningText}
                    variant='h5'
                >
                    <Trans
                        i18nKey='NoChangeMsg'
                        values={{
                            accountNumber: `*****${billpay.accountNumberLast4}`,
                        }}
                    >
                        <strong>
                            This machine cannot return money. All cash
                            will
                            <br />
                            be applied to account ending in{' '}
                            {billpay.accountNumberLast4}
                        </strong>
                    </Trans>
                </Typography>
            </div>
            <div className={classes.amounts}>
                <AmountDisplay
                    title={t('Past Balance Due:')}
                    value={billpay.pastBalanceDueLeft}
                    isAllPaid={billpay.pastBalanceDueLeft <= 0}
                />
                <AmountDisplay
                    title={t('Current Balance Due:')}
                    value={billpay.currentBalanceDueLeft}
                    isAllPaid={billpay.currentBalanceDueLeft <= 0}
                />
                {billpay.hasNextBalanceDue && (
                    <AmountDisplay
                        title={t('Next Balance Due:')}
                        value={billpay.nextBalanceDue}
                        isAllPaid={null}
                    />
                )}
                <hr className={classes.underline} />
                <AmountDisplay
                    title={t('Total Amount Due:')}
                    value={billpay.totalAmountDueLeft}
                    isAllPaid={billpay.totalAmountDueLeft <= 0}
                />
                {billpay.hasInsertedCash && (
                    <div className={classes.amountInserted}>
                        <Typography
                            variant='h4'
                            className={classes.insertedAmountTitle}
                        >
                            {t('Amount Inserted')}
                        </Typography>
                        <Typography
                            variant='h4'
                            className={classes.insertedAmountValue}
                        >
                            ${billpay.amountInserted.toFixed(2)}
                        </Typography>
                    </div>
                )}
            </div>
        </div>
    );
});

export default withTranslation()(withStyles(styles)(PaymentBody));